import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import PortalHeader from '../../assessment-portal/components/portal-header';
import { ADHD_BASE } from '../data/constants';
import { formatAppointmentWhen, parseAppointmentDateTime } from '../utils/appointment-display';
import { formatClinicianDisplay } from '../utils/clinician-display';
import { allDoneContent } from '../utils/completion-copy';
import {
  buildAdhdIntakeCalendarEvent,
  openGoogleCalendarEvent,
} from '../utils/google-calendar';

interface CompletionPageProps {
  variant?: 'default' | 'all-done'
  isSuccess: boolean
  errorMessage?: string
  firstName?: string
  email?: string
  clinicianName?: string
  clinicianTitle?: string
  startDateTime?: string
}

const formatDateFromIso = (isoStr: string): string => {
  const date = parseAppointmentDateTime(isoStr);
  if (!date) return '—';
  return date.toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const formatTimeFromIso = (isoStr: string): string => {
  const date = parseAppointmentDateTime(isoStr);
  if (!date) return '—';
  return date.toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const ClinicianIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="8" r="3.6" />
    <path d="M5 20c0-3.7 3.1-6.2 7-6.2s7 2.5 7 6.2" />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
    <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 13l4 4L19 7" />
  </svg>
);

const NoteInfoIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8h.01M11 12h1v4h1" />
  </svg>
);

const AddToCalendarIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
    <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
  </svg>
);

const CompletionPage: FC<CompletionPageProps> = ({
  variant = 'default',
  isSuccess,
  errorMessage,
  firstName,
  email,
  clinicianName,
  clinicianTitle,
  startDateTime,
}) => {
  if (!isSuccess) {
    return (
      <div className="adhd-portal min-h-screen flex flex-col bg-page">
        <PortalHeader />
        <div className="flex-1 flex items-start justify-center px-4 py-10">
          <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl overflow-hidden p-8 text-center">
            <div className="text-5xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold mb-2 text-brand-dark">
              Submission Failed
            </h2>
            <p className="text-slate-600 text-sm leading-relaxed mb-6">
              {errorMessage || 'We encountered an issue submitting your request.'}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="w-full py-2.5 rounded-xl text-sm font-semibold border-2 border-slate-200 text-slate-500 hover:bg-slate-50 transition-all"
              aria-label="Try again"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'all-done') {
    const clinicianLabel = clinicianName
      || formatClinicianDisplay(clinicianName ?? '', clinicianTitle ?? '', 'Your clinician');
    const displayFirstName = firstName
      ? `${firstName.charAt(0).toUpperCase()}${firstName.slice(1)}`
      : 'there';
    const { hasAppointment, subtitle } = allDoneContent(startDateTime);

    const handleAddToCalendar = () => {
      if (!startDateTime) return;
      openGoogleCalendarEvent(buildAdhdIntakeCalendarEvent(startDateTime, clinicianName ?? ''));
    };

    return (
      <div className="adhd-portal adhd-done-page min-h-screen flex flex-col bg-page">
        <PortalHeader />
        <main className="flex-1 flex items-start justify-center px-4 py-8">
          <div className="adhd-done-shell">
            <div className="adhd-done-hero">
              <div className="adhd-done-hero-icon">
                <CheckIcon />
              </div>
              <h1 className="adhd-done-hero-title">
                All done,
                {' '}
                {displayFirstName}
              </h1>
              <p className="adhd-done-hero-subtitle">
                {subtitle}
              </p>
            </div>

            {hasAppointment && (
            <>
              <div className="adhd-done-details">
                <div className="adhd-done-detail-row">
                  <span className="adhd-done-detail-icon">
                    <ClinicianIcon />
                  </span>
                  <div>
                    <div className="adhd-done-detail-label">{clinicianLabel}</div>
                  </div>
                </div>
                <div className="adhd-done-detail-row adhd-done-detail-row--bt">
                  <span className="adhd-done-detail-icon">
                    <CalendarIcon />
                  </span>
                  <div>
                    <div className="adhd-done-detail-label">
                      {startDateTime ? formatAppointmentWhen(startDateTime) : '—'}
                    </div>
                    <div className="adhd-done-detail-sub">Telehealth video appointment</div>
                  </div>
                </div>
              </div>

              <div className="adhd-done-reassure">
                <span className="adhd-done-reassure-icon">
                  <NoteInfoIcon />
                </span>
                <p className="adhd-done-reassure-text">
                  Your clinician will review everything before the session. We&apos;ll text your telehealth link 48 hours before.
                </p>
              </div>

              <button
                type="button"
                onClick={handleAddToCalendar}
                disabled={!startDateTime}
                className="adhd-done-calendar-cta"
              >
                <AddToCalendarIcon />
                Add to calendar
              </button>
            </>
            )}

            <p className="adhd-done-footer">
              Need to change something?
              {' '}
              <Link to={ADHD_BASE} className="adhd-done-footer-link">
                Start a new booking
              </Link>
            </p>
          </div>
        </main>
      </div>
    );
  }

  const summaryRows = [
    {
      icon: '👩‍⚕️',
      label: 'Clinician',
      value: formatClinicianDisplay(clinicianName ?? '', clinicianTitle ?? ''),
    },
    { icon: '📅', label: 'Date', value: formatDateFromIso(startDateTime || '') },
    { icon: '🕐', label: 'Time', value: formatTimeFromIso(startDateTime || '') },
    {
      icon: '📧',
      label: 'Confirmation sent to',
      value: email || 'your registered email',
    },
  ];

  return (
    <div className="adhd-portal min-h-screen flex flex-col bg-page">
      <PortalHeader />
      <div className="flex-1 flex items-start justify-center px-4 py-10">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-brand to-cyan-500" />
          <div className="p-8">
            <div className="text-center mb-6">
              <div className="text-5xl mb-3">✅</div>
              <h2 className="text-2xl font-bold mb-2 text-brand-dark">
                Thank you,
                {' '}
                {firstName || 'there'}
                !
              </h2>
              <p className="text-slate-600 text-sm leading-relaxed">
                Your ADHD/ASD in-take assessment appointment is confirmed.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl divide-y divide-slate-100 mb-5 text-left">
              {summaryRows.map((row) => (
                <div
                  key={row.label}
                  className="flex items-start gap-3 px-4 py-3"
                >
                  <span className="text-lg mt-0.5 flex-shrink-0">
                    {row.icon}
                  </span>
                  <div>
                    <div className="text-xs text-slate-400 mb-0.5">
                      {row.label}
                    </div>
                    <div className="text-sm font-semibold text-slate-800">
                      {row.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompletionPage;
